import DeleteIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import { Upload } from 'components/FileUpload'
import FileUploader from 'components/FileUploader'
import { PPDialog } from 'components/PPDialog'
import { FileUploaderView } from 'interfaces'
import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { deleteFile, setComposerBulkUploadFileUrl } from 'services/compose'
import { composerBulkFileSelector, composerSelectedProfilesSelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { noop } from 'utils/noop'
import styles from './ComposerBulkUploadBlock.pcss'

const MAX_BULK_POSTS = 3000

interface ComposerBulkUploadBlockProps {
  className?: string
  onUploadStarted: () => void
  onFileUploaded: () => void
  onUploadError: (msg?: string) => void
}

export function ComposerBulkUploadBlock(props: ComposerBulkUploadBlockProps) {
  const intl = useIntl()
  const { onFileUploaded } = props
  const selectedProfiles = useSelector(composerSelectedProfilesSelector)
  const [file, setFile] = useState<File | null>(null)
  const [prevUrl, setPrevUrl] = useState('')
  const [uploaderKey, setUploaderKey] = useState(0)
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [warningDialogOpen, setWarningDialogOpen] = useState(false)
  const bulkUploadFile = useSelector(composerBulkFileSelector)

  const { onUploadError } = props

  useEffect(() => {
    if (!bulkUploadFile) {
      setUploaderKey(current => current + 1)
    }
  }, [bulkUploadFile])

  const clearFile = useCallback(() => {
    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    setUploaderKey(current => current + 1)
  }, [dispatch])

  const onError = (e?: string) => {
    const msg = e || intl.formatMessage({ id: 'composer.errors.upload-generic' })
    dispatch(message(msg, 'error'))
  }

  const onFileSelected = (f: File) => {
    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    setFile(f)
    if (prevUrl) {
      dispatch(deleteFile(prevUrl))
    }
  }

  const onFileUploadError = useCallback(() => {
    dispatch(message(intl.formatMessage({ id: 'uploads.error.upload-failed' }), 'error'))
    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    onUploadError()
  }, [dispatch, intl, onUploadError])

  const handleFileUploaded = useCallback((url: string, response: any) => {
    const rows: number = response?.rows
    if (rows === 0) {
      onUploadError(intl.formatMessage({ id: 'uploads.error.empty-file' }))
      clearFile()
      return
    }
    if (rows) {
      const postsCount = rows * selectedProfiles.length
      if (postsCount > MAX_BULK_POSTS) {
        setWarningDialogOpen(true)
      }

      dispatch(message(intl.formatMessage({ id: 'uploads.notifications.file-uploaded' }), 'success'))
      setPrevUrl(url)
      dispatch(setComposerBulkUploadFileUrl({ url, name: file?.name, rows }))
      onFileUploaded()
    } else {
      onFileUploadError()
    }
  }, [dispatch, intl, selectedProfiles.length, onFileUploaded, onFileUploadError, file, onUploadError, clearFile])

  const clearFileAndCloseWarningDialog = () => {
    setWarningDialogOpen(false)
    setUploaderKey(current => current + 1)
    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
  }

  const closeWarningDialog = () => {
    setWarningDialogOpen(false)
  }

  return (
    <div className={`${styles.block} ${props.className || ''}`}>
      {file && (
        <Upload
          file={file}
          onUploadStarted={props.onUploadStarted}
          onError={onFileUploadError}
          onProgress={noop}
          onSuccess={handleFileUploaded}
        />
      )}
      <div>
        <FileUploader
          key={uploaderKey}
          view={FileUploaderView.Bulk}
          className={styles.uploader}
          onError={onError}
          clearExcelFileButton={prevUrl ? (
            <Tooltip key="btn-remove" title={intl.formatMessage({ id: 'composer.actions.remove-bulk-file' })}>
              <DeleteIcon fontSize="small" className={styles['btn-delete']} onClick={clearFile} />
            </Tooltip>
          ) : undefined}
          onFileUploaded={onFileSelected}
        />
      </div>
      <PPDialog
        open={warningDialogOpen}
        dismissible={false}
        size="lg"
        title={<FormattedMessage id="uploads.bulk.rows-limit-warning.title" />}
        footer={
          <div className={styles.actions}>
            <Button onClick={clearFileAndCloseWarningDialog}>
              <FormattedMessage id="uploads.bulk.rows-limit-warning.action-cancel" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={styles['btn-submit']}
              onClick={closeWarningDialog}
            >
              <FormattedMessage id="uploads.bulk.rows-limit-warning.action-accept" />
            </Button>
          </div>
        }
      >
        <div className={styles.message}>
          <FormattedMessage id="uploads.bulk.rows-limit-warning.message" values={{ count: MAX_BULK_POSTS }} />
          <p><FormattedMessage id="uploads.bulk.rows-limit-warning.note" values={{ count: MAX_BULK_POSTS }} /></p>
        </div>
      </PPDialog>
    </div>
  )
}

export default ComposerBulkUploadBlock
